import { ExternalLink } from 'lucide-react';
import { ROUTES } from '../../routes/consts';
import { StepConfig } from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { FORM_IDS, STEP_IDS } from './consts';
import { CarouselStepConfig } from './types';
import addAvatar from '/images/gitlab-installation/add-avatar.png';
import copyClientIdAndSecret from '/images/gitlab-installation/copy-client-id-and-secret.png';
import createApp from '/images/gitlab-installation/create-app.png';
import createGitlabAccount from '/images/gitlab-installation/create-gitlab-account.png';
import inviteUser from '/images/gitlab-installation/invite-user.png';

const spawnSteps = spawn('dialogs.gitlab-installation.steps');

export const getSteps = (): StepConfig[] => [
  {
    id: STEP_IDS.BEFORE_YOU_BEGIN,
    path: ROUTES.GITLAB_INSTALLATION_STEP_1,
    title: spawnSteps('before-you-begin.title'),
    shouldShowBackButton: false,
    shouldShowCancelButton: true,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CREATE_APP_GUIDE,
    path: ROUTES.GITLAB_INSTALLATION_STEP_2,
    title: spawnSteps('create-app-guide.title'),
    shouldShowBackButton: true,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CONFIGURE_APP,
    path: ROUTES.GITLAB_INSTALLATION_STEP_3,
    title: spawnSteps('configure-app.title'),
    formId: FORM_IDS.CONFIGURE_APP,
    shouldShowBackButton: true,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CREATE_SERVICE_ACCOUNT_GUIDE,
    path: ROUTES.GITLAB_INSTALLATION_STEP_4,
    title: spawnSteps('create-service-account-guide.title'),
    formId: FORM_IDS.CREATE_SERVICE_ACCOUNT,
    shouldShowBackButton: true,
    nextLabel: spawnSteps('create-service-account-guide.next-label'),
    nextLabelIcon: ExternalLink,
    isNextInitiallyEnabled: false,
  },
  {
    id: STEP_IDS.CONNECT_REPOSITORIES,
    path: ROUTES.GITLAB_INSTALLATION_STEP_5,
    title: spawnSteps('connect-repositories.title'),
    formId: FORM_IDS.CONNECT_REPOSITORIES,
    shouldShowBackButton: true,
    nextLabel: spawnSteps('connect-repositories.next-label'),
    isNextInitiallyEnabled: false,
  },
  {
    id: STEP_IDS.CHECK_CONNECTION,
    path: ROUTES.GITLAB_INSTALLATION_STEP_6,
    title: spawnSteps('check-connection.title'),
    shouldShowBackButton: true,
    isNextInitiallyEnabled: false,
  },
  {
    id: STEP_IDS.ENABLE_QODO,
    path: ROUTES.GITLAB_INSTALLATION_STEP_7,
    title: spawnSteps('enable-qodo.title'),
    shouldShowBackButton: true,
    nextLabel: spawnSteps('enable-qodo.next-label'),
  },
];

export const getMTSteps = (): StepConfig[] => [
  {
    id: STEP_IDS.BEFORE_YOU_BEGIN,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_1,
    title: spawnSteps('before-you-begin.title'),
    shouldShowBackButton: false,
    shouldShowCancelButton: true,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CREATE_SERVICE_ACCOUNT_GUIDE,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_2,
    title: spawnSteps('create-service-account-guide.title'),
    formId: FORM_IDS.CREATE_SERVICE_ACCOUNT,
    shouldShowBackButton: true,
    isNextInitiallyEnabled: false,
  },
  {
    id: STEP_IDS.CONFIGURE_APP,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_3,
    title: spawnSteps('configure-app-mt.title'),
    formId: FORM_IDS.CONFIGURE_MT_APP,
    shouldShowBackButton: true,
    nextLabel: spawnSteps('configure-app-mt.next-label'),
    nextLabelIcon: ExternalLink,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CONNECT_REPOSITORIES,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_4,
    title: spawnSteps('connect-repositories.title'),
    formId: FORM_IDS.CONNECT_REPOSITORIES,
    shouldShowBackButton: true,
    nextLabel: spawnSteps('connect-repositories.next-label'),
    isNextInitiallyEnabled: false,
  },
  {
    id: STEP_IDS.CHECK_CONNECTION,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_5,
    title: spawnSteps('check-connection.title'),
    shouldShowBackButton: true,
    isNextInitiallyEnabled: false,
  },
  {
    id: STEP_IDS.ENABLE_QODO,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_6,
    title: spawnSteps('enable-qodo.title'),
    shouldShowBackButton: true,
    nextLabel: spawnSteps('enable-qodo.next-label'),
  },
];

export const getMTTokenSteps = (): StepConfig[] => [
  {
    id: STEP_IDS.BEFORE_YOU_BEGIN,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_1,
    title: spawnSteps('before-you-begin.title'),
    shouldShowBackButton: false,
    shouldShowCancelButton: true,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CONFIGURE_TOKEN,
    path: ROUTES.GITLAB_MT_INSTALLATION_CONFIGURE_TOKEN,
    title: spawnSteps('configure-token.title'),
    formId: FORM_IDS.CONFIGURE_TOKEN,
    shouldShowBackButton: true,
    // Next submits the form (formId), so it must start enabled — onSuccess advances.
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.ENABLE_QODO,
    path: ROUTES.GITLAB_MT_INSTALLATION_STEP_6,
    title: spawnSteps('enable-qodo.title'),
    shouldShowBackButton: true,
    nextLabel: spawnSteps('enable-qodo.next-label'),
  },
];

export const getTokenSteps = (): StepConfig[] => [
  {
    id: STEP_IDS.BEFORE_YOU_BEGIN,
    path: ROUTES.GITLAB_INSTALLATION_STEP_1,
    title: spawnSteps('before-you-begin.title'),
    shouldShowBackButton: false,
    shouldShowCancelButton: true,
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.CONFIGURE_TOKEN,
    path: ROUTES.GITLAB_INSTALLATION_CONFIGURE_TOKEN,
    title: spawnSteps('configure-token.title'),
    formId: FORM_IDS.CONFIGURE_TOKEN,
    shouldShowBackButton: true,
    // Next submits this form (formId), so it must start enabled — otherwise the
    // user can never trigger the provision. onSuccess advances the step.
    isNextInitiallyEnabled: true,
  },
  {
    id: STEP_IDS.ENABLE_QODO,
    path: ROUTES.GITLAB_INSTALLATION_STEP_7,
    title: spawnSteps('enable-qodo.title'),
    shouldShowBackButton: true,
    nextLabel: spawnSteps('enable-qodo.next-label'),
  },
];

export const getCreateServiceAccountGuideCarouselSteps =
  (): CarouselStepConfig[] => [
    { id: 'invite-user', image: inviteUser },
    { id: 'create-account', image: createGitlabAccount },
    { id: 'add-avatar', image: addAvatar },
  ];

export const getCreateAppGuideCarouselSteps = (): CarouselStepConfig[] => [
  { id: 'create-app', image: createApp },
  { id: 'copy-client-id-and-secret', image: copyClientIdAndSecret },
];
